import { Icon, Row, Col, Button, Modal, Form, Select, Input } from "ant-design-vue";
import { Component, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { fmtMsg, MessageHelper, NotificationType, ActionItem } from "commonui";
import { CommonLocale, ConversationTemplateLocale, OpenCloseLocale } from "@/locales/localeid";
import {
	ConversationAssocResponseModel,
	ConversationTemplateModel,
	ConversationTemplateService,
	MasterConversationType
} from "@/services/conversation-templates";
import { deepClone, disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import ConversationTemplateState from "@/modules/conversation-template";
import { AccessLevel } from "@/util/enum";

interface GridTemplateModel extends ConversationTemplateModel {
	typeValue?: string;
}
@Component({
	components: {
		Grid,
		Icon,
		Row,
		Col,
		Button,
		ActionItem,
		Modal,
		Form,
		FormItem: Form.Item,
		Select,
		Option: Select.Option,
		TextArea: Input.TextArea
	}
})
export default class OpenClose extends Vue {
	private templateService = ConversationTemplateService();
	public templateState = getModule(ConversationTemplateState);
	public templates: GridTemplateModel[] = [];
	public filteredTemplates: GridTemplateModel[] = [];
	public loading: boolean = false;
	public saving: boolean = false;
	public format = fmtMsg;
	public locale = OpenCloseLocale;
	public commonLocale = CommonLocale;
	public templateLocale = ConversationTemplateLocale;
	public showAddEditModal = false;
	public showDeleteModal = false;
	public form;
	public openCloseTypes = [
		{
			id: MasterConversationType.Open,
			display: this.format(this.locale.OpenCloseOpen)
		},
		{
			id: MasterConversationType.Close,
			display: this.format(this.locale.OpenCloseTypeClose)
		},
		{
			id: MasterConversationType.Other,
			display: this.format(this.locale.OpenCloseOther)
		}
	];
	public typeLabelCol = { span: 2 };
	public selectedTemplate: ConversationTemplateModel | null = null;
	public deleteConversationId: number = 0;
	public maxTextLength = 500;
	public selectedTag = 1;
	public selectedFilter = -1;
	public filterTypes = [
		{
			id: -1,
			display: this.format(this.locale.OpenCloseAll)
		},
		...this.openCloseTypes
	];
	refConversations: ConversationAssocResponseModel[] = [];
	public accessLevel = AccessLevel;
	public enableSaveButton: boolean = false;

	onEditOpenClose() {
		this.enableSaveButton = true;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	created() {
		this.form = this.$form.createForm(this, { name: "form" });
		this.getOpenCloseTemplates();
		if (this.templateState && this.templateState.conversationTags && this.templateState.conversationTags.length) {
			this.selectedTag = this.templateState.conversationTags[0].id;
		}
	}

	onFilterSelect(value: MasterConversationType | -1) {
		if (value === -1) {
			this.filteredTemplates = this.templates;
		} else {
			this.filteredTemplates = this.templates.filter((template) => template.type === value);
		}
	}

	getOpenCloseTemplates() {
		this.loading = true;

		this.templateService
			.getTemplates({
				types: [MasterConversationType.Open, MasterConversationType.Close, MasterConversationType.Other]
			})
			.then((templates) => {
				const gridTemplate: GridTemplateModel[] = [...templates];
				this.templates = gridTemplate.map((template) => {
					switch (template.type) {
						case 3:
							template.typeValue = this.format(this.locale.OpenCloseOpen);
							break;
						case 4:
							template.typeValue = this.format(this.locale.OpenCloseTypeClose);
							break;
						case 5:
							template.typeValue = this.format(this.locale.OpenCloseOther);
							break;
					}
					return template;
				});
				this.templates = templates;
				this.filteredTemplates = templates;
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	getTypeCellValue(cell) {
		if (!cell || !cell.item || !cell.item.type) {
			return "";
		}

		return this.getTypeValue(cell.item.type);
	}

	getTypeValue(value) {
		switch (value) {
			case MasterConversationType.Open:
				return this.format(this.locale.OpenCloseOpen);

			case MasterConversationType.Close:
				return this.format(this.locale.OpenCloseTypeClose);

			case MasterConversationType.Other:
				return this.format(this.locale.OpenCloseOther);

			default:
				return "";
		}
	}

	onAddEditModalShow(template?: ConversationTemplateModel | null) {
		this.showAddEditModal = true;

		if (template) {
			this.selectedTemplate = template;
		} else {
			this.selectedTemplate = null;
		}
	}

	onAddEditModalCancel() {
		this.enableSaveButton = false;
		this.showAddEditModal = false;
		this.selectedTemplate = null;
	}

	onAddEdit() {
		this.form.validateFields((err, values) => {
			if (!err) {
				this.saving = true;
				if (
					this.selectedTemplate &&
					this.selectedTemplate.conversationId &&
					(this.selectedTemplate.type === MasterConversationType.Open ||
						this.selectedTemplate.type === MasterConversationType.Close)
				) {
					this.templateService
						.getCompletedConvRefs(this.selectedTemplate.conversationId)
						.then((completedConvs) => {
							this.saving = false;
							if (completedConvs.length) {
								Modal.confirm({
									title: fmtMsg(ConversationTemplateLocale.TemplateConversationUsedMsg, {
										0: completedConvs.length
									}),
									content: "",
									onOk: () => {
										this.saving = true;
										this.createOrUpdate(values);
									},
									onCancel: () => {}
								});
							} else {
								this.createOrUpdate(values);
							}
						});
				} else {
					this.createOrUpdate(values);
				}
			}
		});
	}

	createOrUpdate(values: any[]) {
		if (this.selectedTemplate) {
			this.updateTemplate(values);
		} else {
			this.createTemplate(values);
		}
	}

	onDelete(conversationId: number) {
		this.deleteConversationId = conversationId;
		const template = this.templates.find((t) => t.id === this.deleteConversationId);

		if (
			template &&
			template.conversationId &&
			(template.type === MasterConversationType.Open || template.type === MasterConversationType.Close)
		) {
			this.saving = true;
			this.templateService.getConvTemplateRefs(template.conversationId).then((convRefs) => {
				this.saving = false;

				if (convRefs.length) {
					this.refConversations = convRefs;
				}

				this.showDeleteModal = true;
			});
		}
	}

	onDeleteCancel() {
		this.showDeleteModal = false;
		this.deleteConversationId = 0;
		this.refConversations = [];
	}

	sortByText(fallback1, fallback2) {
		if (fallback1.text < fallback2.text) {
			return -1;
		}
		if (fallback1.text > fallback2.text) {
			return 1;
		}

		return 0;
	}

	createTemplate(values) {
		this.templateService
			.createTemplate(values)
			.then((createdId) => {
				this.saving = false;
				this.showAddEditModal = false;
				const { text, type } = values;
				const createdFallback: ConversationTemplateModel = {
					id: createdId,
					text: text.trim(),
					type
				};

				this.templates = this.templates.concat([createdFallback]).sort(this.sortByText);
				this.onFilterSelect(this.selectedFilter);

				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.OpenCloseAddSuccess));
			})
			.catch(() => {
				this.saving = false;
			});
	}

	updateTemplate(values) {
		if (!this.selectedTemplate) {
			return;
		}

		const text = values.text.trim();

		this.templateService
			.updateTemplate({ id: this.selectedTemplate.id, ...values, text })
			.then(() => {
				this.saving = false;
				this.showAddEditModal = false;
				let list = deepClone(this.templates);

				const index = list.findIndex((fallback) => fallback.id === this.selectedTemplate?.id);
				const editedFallback = { ...list[index], ...values, text };

				list = [...list.slice(0, index), editedFallback, ...list.slice(index + 1, list.length)].sort(
					this.sortByText
				);

				this.templates = list;
				this.selectedTemplate = null;
				this.onFilterSelect(this.selectedFilter);

				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.OpenCloseUpdateSuccess));
			})
			.catch(() => {
				this.saving = false;
			});
	}

	remainingCount(): number {
		var text = this.form.getFieldValue("text");

		const calculateRemainingLength = (value: string) => {
			if (!value) {
				return this.maxTextLength;
			}

			if (this.maxTextLength - value.length >= 0) {
				return this.maxTextLength - value.length;
			} else {
				return 0;
			}
		};

		if (this.selectedTemplate && !text) {
			/* 
				// initially we want to set the text value to selected template's text value.
				// typeof text is checked since the value form form comes as undefined but after user starts 
				// editing the textarea the value becomes string and we do not want to again set text's value 
				// as selected template's value.
			*/
			if (typeof text !== "string") {
				text = this.selectedTemplate.text;
			}
		}

		return calculateRemainingLength(text);
	}

	deleteTemplate() {
		if (this.refConversations.length) {
			this.showDeleteModal = false;
			this.deleteConversationId = 0;
			return;
		}

		this.saving = true;
		this.templateService
			.deleteTemplate(this.deleteConversationId)
			.then(() => {
				this.saving = false;
				this.showDeleteModal = false;
				this.templates = this.templates.filter((fallback) => fallback.id !== this.deleteConversationId);
				this.deleteConversationId = 0;
				this.onFilterSelect(this.selectedFilter);

				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.OpenCloseDeleteSuccess));
			})
			.catch(() => {
				this.saving = false;
			});
	}

	requiredTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		value = (value || "").trim();

		if (!value) {
			callback(this.format(this.templateLocale.TemplateValidationText));
			return;
		}

		callback();
	}

	// Checks whether the text of a template is unique for a given type.
	uniqueTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		let list = this.templates;
		let type = this.form.getFieldValue("type");

		if (this.selectedTemplate) {
			list = list.filter((fallback) => fallback.id !== this.selectedTemplate?.id);

			if (!type) {
				type = this.selectedTemplate.type;
			}
		}

		const index = list.findIndex(
			(fallback) => fallback.type === type && fallback.text.trim().toLowerCase() === value.trim().toLowerCase()
		);

		if (index >= 0) {
			callback(fmtMsg(this.templateLocale.TemplateValidationUniqueText));
			return;
		}

		callback();
	}

	typeChangeValidator(rule: any, value: MasterConversationType, callback: (msg?: string) => void) {
		if (!this.selectedTemplate) {
			callback();
			return;
		}

		let type = value || this.form.getFieldValue("type");

		if (!type) {
			type = this.selectedTemplate.type;
		}

		if (
			this.selectedTemplate.conversationId &&
			type !== this.selectedTemplate.type &&
			(this.selectedTemplate.type === MasterConversationType.Open ||
				this.selectedTemplate.type === MasterConversationType.Close)
		) {
			return this.templateService.getConvTemplateRefs(this.selectedTemplate.conversationId).then((convRefs) => {
				if (convRefs.length) {
					callback(fmtMsg(OpenCloseLocale.OpenCloseTypeUsedMsg));
				} else {
					callback();
				}
			});
		}

		callback();
	}

	// Validates tag and its count.
	tagValidator(rule: any, value: string, callback: (msg?: string) => void) {
		var regex = /\{.*?\}/g;
		const text = this.form.getFieldValue("text");
		const isTagValid = this.isTagValid(regex, text);

		if (!isTagValid) {
			callback(this.format(OpenCloseLocale.OpenCloseValidationTag));
			return;
		}

		const isTagCountValid = this.isTagCountValid(text);

		if (!isTagCountValid) {
			callback(this.format(OpenCloseLocale.OpenCloseValidationTag));
			return;
		}

		callback();
	}

	onAddTag() {
		const selectedTag = this.templateState.conversationTags.find(
			(tag) => tag.id === ((this.selectedTag as unknown) as number)
		);

		if (selectedTag) {
			let text: string = this.form.getFieldValue("text") || "";
			let tag = `{${selectedTag.value}}`;

			if (text && !text.endsWith(" ")) {
				tag = ` ${tag}`;
			}

			text += tag;

			this.form.setFieldsValue({ text: text.substr(0, this.maxTextLength) });
			this.form.validateFields(["text"]);
		}
	}

	// Checks for invalid conversation tag, returns true if valid, false otherwise.
	isTagValid(regex, text) {
		const result = regex.exec(text);

		if (result && result[0]) {
			var matchText = result[0];

			if (this.templateState.conversationTags.some((tag) => `{${tag.value}}` === matchText)) {
				return this.isTagValid(regex, text); // recursively validate all tags.
			} else {
				return false;
			}
		}

		return true;
	}

	// Checks if count of opening tags '{' match is equal to closing tags '}', true if matched, false otherwise.
	isTagCountValid(text) {
		const openTagRegex = /\{/g;
		const closeTagRegex = /\}/g;
		let openCount = 0;
		let closeCount = 0;
		let openResult;
		let closeResult;

		while ((openResult = openTagRegex.exec(text))) {
			openCount++;
		}

		while ((closeResult = closeTagRegex.exec(text))) {
			closeCount++;
		}

		return openCount === closeCount;
	}
}
